import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Form } from 'react-bootstrap';
import type { User } from '../../../types';
import type { ChangeCatalogItem, ChangeWorkGroup } from '../../../types/changes';
import { changesApi } from '../../../services/changesApi';
import { getApiErrorMessage } from '../../../utils/apiError';
import {
  externalLineKey,
  internalSlotLabel,
  reviewChipLabel,
  slotReviewKey,
} from '../changeCreateReview';
import {
  fullCreateSubtaskReviewPermissions,
  type CreateSubtaskReviewPermissions,
} from '../changeSubtaskCreateReviewPermissions';
import { ASSIGNMENT_PESSOA, ASSIGNMENT_SETOR } from '../changeSubtaskAssignment';
import { emptyIncidentDraft, incidentAllowsAttachments, type IncidentSendDraft, type IncidentWorkflowOption } from '../incidentQuickSend';
import ChangeFormFieldBuilder from './ChangeFormFieldBuilder';
import ChangeInstanceChecklistEditor from './ChangeInstanceChecklistEditor';
import ChangeWorkGroupIncidentModal, {
  hydrateIncidentGroupDraft,
} from './ChangeWorkGroupIncidentModal';

import type { ReviewQuestionDraft } from '../changeInstanceChecklistFields';
import { canMutateInternalPackageSlot } from '../changeInternalSlotOwnership';
import { isChangeManager } from '../changePermissions';
import { useAuth } from '../../../context/AuthContext';
import { useModal } from '../../../context/ModalContext';
import {
  persistInternalDraftIfNeeded,
  type InternalSubtaskTemplatePersistMode,
} from '../changePersistInternalSubtaskTemplate';

export type { ReviewQuestionDraft };

export interface InternalReviewDraft {
  selected: boolean;
  title: string;
  comments: string;
  task_type_id: number | '';
  assignment_kind: typeof ASSIGNMENT_SETOR | typeof ASSIGNMENT_PESSOA;
  assignee_id: number | '';
  questions: ReviewQuestionDraft[];
  /** Subtarefa já persistida na mudança (somente leitura no pacote). */
  existingSubtaskId?: number;
  locked?: boolean;
}

export interface ExternalReviewDraft extends IncidentSendDraft {
  selected: boolean;
  open_in_incidents?: boolean;
  incident_kind?: '' | 'si' | 'infra_nuvem' | 'infra_hosting';
  incident_attachment_allowed?: boolean;
  existingSubtaskId?: number;
  locked?: boolean;
}

export function isSiIncidentArea(area: ChangeWorkGroup): boolean {
  const kind = String(area.incident_kind || '').trim().toLowerCase();
  if (kind === 'si') return true;
  const name = (area.name || '').toUpperCase();
  return name.includes('CSI') && !name.includes('CINFRA');
}

type ReviewPanel = 'list' | 'edit-internal' | 'edit-external' | 'add-internal' | 'add-external';

interface Props {
  internals: ChangeWorkGroup[];
  externals: ChangeWorkGroup[];
  leftoverInternals: ChangeWorkGroup[];
  leftoverExternals: ChangeWorkGroup[];
  internalDrafts: Record<string, InternalReviewDraft>;
  externalDrafts: Record<string, ExternalReviewDraft>;
  workflows: IncidentWorkflowOption[];
  incidentTargetGroups?: Array<{ id: number; name: string }>;
  taskTypes: ChangeCatalogItem[];
  users: User[];
  onToggleInternal: (reviewKey: string, selected: boolean) => void;
  onPatchInternal: (reviewKey: string, patch: Partial<InternalReviewDraft>) => void;
  onToggleExternal: (lineKey: string, selected: boolean) => void;
  onPatchExternal: (lineKey: string, patch: Partial<ExternalReviewDraft>) => void;
  onAddInternal: (group: ChangeWorkGroup) => string | undefined | void;
  onAddExternal: (group: ChangeWorkGroup) => string | undefined | void;
  onConvertInternalToExternal: (group: ChangeWorkGroup) => void;
  onInternalSettings?: (
    group: ChangeWorkGroup,
    patch: { auto_subtask_on_normal?: boolean; auto_subtask_allow_thread?: boolean },
  ) => void;
  onRemoveInternal: (reviewKey: string) => void;
  onRemoveExternal: (lineKey: string) => void;
  onPanelChange?: (editing: boolean) => void;
  /** Quando false, oculta toda a revisão de subtarefas internas (emergencial, correção). */
  allowInternalSubtasks?: boolean;
  /** Na aba Subtarefas (pacote manual): permite internas mesmo com tipo sem disparo automático. */
  allowManualInternalPackage?: boolean;
  /** Pacote manual na aba Subtarefas: várias linhas por área. */
  allowRepeatSubtaskLines?: boolean;
  /** Pacote na mudança existente: não edita cadastro do grupo/modelo global. */
  instancePackageMode?: boolean;
  reviewPermissions?: CreateSubtaskReviewPermissions;
  templatePersistMode?: InternalSubtaskTemplatePersistMode | null;
  onTemplateSaved?: (templateId: number, groupId: number) => void;
  onTemplateDeleted?: (templateId: number) => void;
}

const emptyInternal = (group: ChangeWorkGroup): InternalReviewDraft => ({
  selected: true,
  title: (group.internal_subtask_template_name || group.name || '').trim() || 'Checklist',
  comments: '',
  task_type_id: '',
  assignment_kind: ASSIGNMENT_SETOR,
  assignee_id: '',
  questions: [],
});

const ChangeCreateSubtaskReview: React.FC<Props> = ({
  internals,
  externals,
  leftoverInternals,
  leftoverExternals,
  internalDrafts,
  externalDrafts,
  workflows,
  incidentTargetGroups = [],
  taskTypes,
  users,
  onToggleInternal,
  onPatchInternal,
  onToggleExternal,
  onPatchExternal,
  onAddInternal,
  onAddExternal,
  onConvertInternalToExternal,
  onInternalSettings,
  onRemoveInternal,
  onRemoveExternal,
  onPanelChange,
  allowInternalSubtasks = true,
  allowManualInternalPackage = false,
  allowRepeatSubtaskLines = false,
  instancePackageMode = false,
  reviewPermissions = fullCreateSubtaskReviewPermissions(),
  templatePersistMode = null,
  onTemplateSaved,
  onTemplateDeleted,
}) => {
  const { user } = useAuth();
  const { showConfirm } = useModal();
  const manager = isChangeManager(user);

  const confirmRemoveInternal = async (lineKey: string, label: string) => {
    const ok = await showConfirm({
      title: 'Remover subtarefa interna',
      message: `Remover «${label}» desta lista? Você poderá adicionar de novo antes de confirmar.`,
      variant: 'danger',
      confirmLabel: 'Remover',
      cancelLabel: 'Cancelar',
    });
    if (ok) onRemoveInternal(lineKey);
  };

  const confirmRemoveExternal = async (lineKey: string, label: string) => {
    const ok = await showConfirm({
      title: 'Remover chamado',
      message: `Remover «${label}» desta lista? Você poderá adicionar de novo antes de confirmar.`,
      variant: 'danger',
      confirmLabel: 'Remover',
      cancelLabel: 'Cancelar',
    });
    if (ok) onRemoveExternal(lineKey);
  };
  const { canSelect, canEditInternal, canEditExternal, canRemove, canAdd } = reviewPermissions;
  const rowCanMutateInternal = (group: ChangeWorkGroup) =>
    canMutateInternalPackageSlot(group, {
      currentUserId: user?.id,
      isChangeManager: manager,
      allowRequesterPackageEdit: instancePackageMode && canEditInternal,
    });
  const showSelectionCheckboxes = canSelect;

  const renderSelectionCheckbox = (opts: {
    id: string;
    ariaLabel: string;
    checked: boolean;
    locked: boolean;
    onChange?: (checked: boolean) => void;
  }) => {
    if (!showSelectionCheckboxes) return null;
    if (opts.locked) {
      if (!instancePackageMode) return null;
      return (
        <Form.Check
          type="checkbox"
          id={opts.id}
          aria-label={opts.ariaLabel}
          checked
          disabled
          title="Já está na mudança"
        />
      );
    }
    return (
      <Form.Check
        type="checkbox"
        id={opts.id}
        aria-label={opts.ariaLabel}
        checked={opts.checked}
        onChange={(event) => opts.onChange?.(event.target.checked)}
      />
    );
  };
  const [panel, setPanel] = useState<ReviewPanel>('list');
  const [editingExternalKey, setEditingExternalKey] = useState<string | null>(null);
  const [editingInternalKey, setEditingInternalKey] = useState<string | null>(null);
  const [addId, setAddId] = useState<string>('');
  const [heldExternal, setHeldExternal] = useState<ChangeWorkGroup | null>(null);
  const [heldInternal, setHeldInternal] = useState<ChangeWorkGroup | null>(null);
  const [internalPersistError, setInternalPersistError] = useState<string | null>(null);
  const [internalPersisting, setInternalPersisting] = useState(false);
  /** Linha recém-incluída via «Incluir e editar» — «Voltar sem alterar» descarta. */
  const [discardExternalOnBackKey, setDiscardExternalOnBackKey] = useState<string | null>(null);
  const [discardInternalOnBackKey, setDiscardInternalOnBackKey] = useState<string | null>(null);

  useEffect(() => {
    onPanelChange?.(panel !== 'list');
  }, [panel, onPanelChange]);

  const showInternalSection =
    allowInternalSubtasks || (instancePackageMode && allowManualInternalPackage);
  const internalLineKey = (group: ChangeWorkGroup) => group.review_key || slotReviewKey(group);
  const selectedInternals = showInternalSection
    ? internals.filter((group) => internalDrafts[internalLineKey(group)]?.selected !== false)
    : [];
  const selectedExternals = externals.filter(
    (group) => externalDrafts[externalLineKey(group)]?.selected !== false,
  );
  const editingInternal =
    internals.find((group) => internalLineKey(group) === editingInternalKey) ||
    (heldInternal && internalLineKey(heldInternal) === editingInternalKey ? heldInternal : undefined);
  const editingExternal =
    externals.find((group) => externalLineKey(group) === editingExternalKey) ||
    (heldExternal && externalLineKey(heldExternal) === editingExternalKey ? heldExternal : undefined);

  const goList = () => {
    setPanel('list');
    setEditingExternalKey(null);
    setEditingInternalKey(null);
    setAddId('');
    setHeldExternal(null);
    setHeldInternal(null);
    setInternalPersistError(null);
    setDiscardExternalOnBackKey(null);
    setDiscardInternalOnBackKey(null);
  };

  const cancelExternalEdit = () => {
    const key = editingExternalKey;
    if (discardExternalOnBackKey && key && discardExternalOnBackKey === key) {
      onRemoveExternal(discardExternalOnBackKey);
    }
    setDiscardExternalOnBackKey(null);
    setPanel('list');
    setEditingExternalKey(null);
    setAddId('');
    setHeldExternal(null);
  };

  const confirmExternalEdit = () => {
    setDiscardExternalOnBackKey(null);
    setPanel('list');
    setEditingExternalKey(null);
    setAddId('');
    setHeldExternal(null);
  };

  const cancelInternalEdit = () => {
    const key = editingInternalKey;
    if (discardInternalOnBackKey && key && discardInternalOnBackKey === key) {
      onRemoveInternal(discardInternalOnBackKey);
    }
    setDiscardInternalOnBackKey(null);
    setPanel('list');
    setEditingInternalKey(null);
    setAddId('');
    setHeldInternal(null);
    setInternalPersistError(null);
  };

  const finishInternalEditAndGoList = () => {
    if (
      !instancePackageMode ||
      !templatePersistMode ||
      !editingInternal ||
      !rowCanMutateInternal(editingInternal)
    ) {
      goList();
      return;
    }
    const key = editingInternal.review_key || slotReviewKey(editingInternal);
    const draft = internalDrafts[key] || emptyInternal(editingInternal);
    if (!(draft.questions || []).length) {
      goList();
      return;
    }
    setInternalPersistError(null);
    setInternalPersisting(true);
    void persistInternalDraftIfNeeded({
      slot: editingInternal,
      draft,
      mode: templatePersistMode,
      canPersist: true,
    })
      .then((savedId) => {
        if (savedId) {
          onTemplateSaved?.(savedId, editingInternal.group_id);
        }
        goList();
      })
      .catch((err: unknown) => {
        setInternalPersistError(
          getApiErrorMessage(err, 'Não foi possível salvar o modelo reutilizável.'),
        );
      })
      .finally(() => setInternalPersisting(false));
  };

  useEffect(() => {
    if (showInternalSection) return;
    if (panel === 'add-internal' || panel === 'edit-internal') {
      setPanel('list');
      setEditingExternalKey(null);
      setEditingInternalKey(null);
      setAddId('');
    }
  }, [showInternalSection, panel]);

  useEffect(() => {
    if (!canAdd && (panel === 'add-internal' || panel === 'add-external')) {
      goList();
      return;
    }
    if (!canEditInternal && panel === 'edit-internal') {
      goList();
      return;
    }
    if (!canEditExternal && panel === 'edit-external') {
      goList();
    }
  }, [canAdd, canEditInternal, canEditExternal, panel]);

  const openEditInternal = (
    reviewKey: string,
    slot?: ChangeWorkGroup,
    opts?: { discardOnBack?: boolean },
  ) => {
    setEditingInternalKey(reviewKey);
    if (slot !== undefined) {
      setHeldInternal(slot);
    }
    setDiscardInternalOnBackKey(opts?.discardOnBack ? reviewKey : null);
    setPanel('edit-internal');
  };

  const openEditExternal = (
    lineKey: string,
    group?: ChangeWorkGroup,
    opts?: { discardOnBack?: boolean },
  ) => {
    setEditingExternalKey(lineKey);
    if (group !== undefined) {
      setHeldExternal(group);
    }
    setDiscardExternalOnBackKey(opts?.discardOnBack ? lineKey : null);
    setPanel('edit-external');
  };

  if ((panel === 'add-internal' || panel === 'add-external') && canAdd) {
    const leftover = panel === 'add-internal' ? leftoverInternals : leftoverExternals;
    const label = panel === 'add-internal' ? 'interna' : 'chamado';
    const emptyHint =
      leftover.length || allowRepeatSubtaskLines
        ? null
        : instancePackageMode
          ? panel === 'add-external'
            ? 'Todas as áreas com chamado no Incidente já estão nesta lista (uma por área). Remova uma linha (Remover) para trocar de área ou use Editar no chamado para personalizar título, fluxo e campos.'
            : 'Todas as áreas/modelos disponíveis já estão nesta lista. Remova uma linha (Remover) para liberar no combo ou escolha outro modelo do mesmo grupo.'
          : 'Todas as áreas ativas já estão nesta lista. Remova uma linha se quiser trocar.';
    return (
      <div>
        <h6 className="mb-2">Incluir subtarefa {label}</h6>
        {emptyHint ? (
          <Alert variant="info" className="mb-3">
            {emptyHint}
          </Alert>
        ) : (
          <>
            <Form.Label htmlFor="review-add-group">Área</Form.Label>
            <Form.Select
              id="review-add-group"
              aria-label={`Área da subtarefa ${label}`}
              value={addId}
              onChange={(event) => setAddId(event.target.value)}
            >
              <option value="">Selecione</option>
              {leftover.map((group) => {
                const optionKey = allowRepeatSubtaskLines
                  ? String(group.group_id)
                  : panel === 'add-internal'
                    ? slotReviewKey(group)
                    : String(group.group_id);
                return (
                  <option key={`${optionKey}-${group.name}`} value={optionKey}>
                    {allowRepeatSubtaskLines || panel === 'add-external'
                      ? group.name
                      : internalSlotLabel(group)}
                  </option>
                );
              })}
            </Form.Select>
          </>
        )}
        <div className="d-flex gap-2 mt-3">
          <Button type="button" variant="outline-secondary" onClick={goList}>
            Voltar à lista
          </Button>
          {emptyHint ? null : (
            <Button
              type="button"
              disabled={!addId}
              onClick={() => {
                const group = leftover.find((item) =>
                  allowRepeatSubtaskLines
                    ? String(item.group_id) === addId
                    : panel === 'add-internal'
                      ? slotReviewKey(item) === addId
                      : String(item.group_id) === addId,
                );
                if (!group) return;
                if (panel === 'add-internal') {
                  const lineKey = onAddInternal(group);
                  if (canEditInternal) {
                    openEditInternal(lineKey || group.review_key || slotReviewKey(group), group, {
                      discardOnBack: true,
                    });
                  } else {
                    goList();
                  }
                } else {
                  const lineKey = onAddExternal(group);
                  if (canEditExternal) {
                    openEditExternal(lineKey || externalLineKey(group), group, {
                      discardOnBack: true,
                    });
                  } else {
                    goList();
                  }
                }
                setAddId('');
              }}
            >
              Incluir e editar
            </Button>
          )}
        </div>
      </div>
    );
  }

  const canEditThisInternal =
    editingInternal && instancePackageMode
      ? rowCanMutateInternal(editingInternal)
      : canEditInternal;

  if (panel === 'edit-internal' && editingInternal && allowInternalSubtasks && canEditThisInternal) {
    const mergedDraft = {
      ...emptyInternal(editingInternal),
      ...internalDrafts[slotReviewKey(editingInternal)],
    };
    if (mergedDraft.locked) {
      return (
        <div>
          <Alert variant="info">
            Esta subtarefa já está na mudança. Para alterar, use <strong>Ver</strong> na aba Subtarefas.
          </Alert>
          <Button variant="outline-secondary" onClick={goList}>Voltar à lista</Button>
        </div>
      );
    }
    const draft = mergedDraft;
    const members = (editingInternal.members || []).filter((member) => member.user_id);
    const patch = (next: Partial<InternalReviewDraft>) =>
      onPatchInternal(slotReviewKey(editingInternal), next);
    return (
      <div>
        <h6 className="mb-2">Editar subtarefa interna</h6>
        <p className="small text-muted mb-3">
          {internalSlotLabel(editingInternal)}
        </p>
        <Form.Label htmlFor="review-internal-title">Título da subtarefa</Form.Label>
        <Form.Control
          id="review-internal-title"
          value={draft.title}
          maxLength={255}
          onChange={(event) => patch({ title: event.target.value })}
        />
        <Form.Label htmlFor="review-internal-type" className="mt-3">
          Tipo da tarefa
        </Form.Label>
        <Form.Select
          id="review-internal-type"
          aria-label="Tipo da tarefa"
          value={draft.task_type_id}
          onChange={(event) =>
            patch({ task_type_id: event.target.value ? Number(event.target.value) : '' })
          }
        >
          <option value="">Selecione o tipo da tarefa</option>
          {taskTypes.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </Form.Select>
        <Form.Label className="mt-3">Enviar para</Form.Label>
        <div className="d-flex flex-wrap gap-3 mb-2">
          <Form.Check
            type="radio"
            id="review-internal-dest-setor"
            name="review-internal-assignment"
            label="Grupo"
            checked={draft.assignment_kind === ASSIGNMENT_SETOR}
            onChange={() => patch({ assignment_kind: ASSIGNMENT_SETOR, assignee_id: '' })}
          />
          <Form.Check
            type="radio"
            id="review-internal-dest-pessoa"
            name="review-internal-assignment"
            label="Pessoa específica"
            checked={draft.assignment_kind === ASSIGNMENT_PESSOA}
            onChange={() => patch({ assignment_kind: ASSIGNMENT_PESSOA })}
          />
        </div>
        {draft.assignment_kind === ASSIGNMENT_PESSOA ? (
          <>
            <Form.Label htmlFor="review-internal-assignee">Pessoa</Form.Label>
            <Form.Select
              id="review-internal-assignee"
              aria-label="Pessoa que vai receber a subtarefa"
              value={draft.assignee_id}
              onChange={(event) =>
                patch({ assignee_id: event.target.value ? Number(event.target.value) : '' })
              }
            >
              <option value="">Selecione a pessoa</option>
              {members.map((member) => (
                <option key={member.user_id} value={member.user_id}>
                  {member.name || `Usuário #${member.user_id}`}
                </option>
              ))}
            </Form.Select>
            <div className="form-text">
              {members.length
                ? 'Somente esta pessoa poderá assumir.'
                : 'Esta área ainda não tem pessoas no cadastro de mudanças.'}
            </div>
          </>
        ) : (
          <div className="form-text">Qualquer membro de {editingInternal.name} assume e responde.</div>
        )}
        <Form.Label htmlFor="review-internal-comments" className="mt-3">
          Comentário inicial
        </Form.Label>
        <Form.Control
          id="review-internal-comments"
          as="textarea"
          rows={3}
          value={draft.comments}
          onChange={(event) => patch({ comments: event.target.value })}
        />
        {internalPersistError ? (
          <Alert variant="danger" className="mt-3 mb-0">
            {internalPersistError}
          </Alert>
        ) : null}
        {instancePackageMode && templatePersistMode ? (
          <Alert variant="info" className="mt-3 mb-0 small">
            Ao clicar em <strong>Salvar e voltar à lista</strong>, o modelo reutilizável é gravado no
            banco (cadastro para gestores; pessoal para solicitantes) e reaparece ao reabrir a modal.
          </Alert>
        ) : null}
        <div className="mt-4">
          <h6 className="text-uppercase small text-muted">Perguntas e campos</h6>
          {instancePackageMode ? (
            <ChangeInstanceChecklistEditor
              key={slotReviewKey(editingInternal)}
              questions={draft.questions || []}
              templateId={editingInternal.internal_subtask_template_id}
              groupId={editingInternal.group_id}
              defaultModelName={draft.title}
              templatePersistMode={
                rowCanMutateInternal(editingInternal) ? templatePersistMode : null
              }
              canDeletePersistedTemplate={
                rowCanMutateInternal(editingInternal) &&
                Boolean(editingInternal.internal_subtask_template_id)
              }
              onTemplateSaved={onTemplateSaved}
              onTemplateDeleted={(id) => {
                onTemplateDeleted?.(id);
                goList();
              }}
              onChange={(next) => patch({ questions: next })}
            />
          ) : (
            <>
              <Form.Check
                type="switch"
                id="review-auto-subtask"
                className="mb-2"
                label="Abrir subtarefa automática"
                checked={Boolean(editingInternal.auto_subtask_on_normal)}
                onChange={() =>
                  onInternalSettings?.(editingInternal, {
                    auto_subtask_on_normal: !editingInternal.auto_subtask_on_normal,
                  })
                }
              />
              {editingInternal.auto_subtask_on_normal ? (
                <Form.Check
                  type="switch"
                  id="review-auto-thread"
                  className="mb-3"
                  label="Permitir comentários e anexos"
                  checked={Boolean(editingInternal.auto_subtask_allow_thread)}
                  onChange={() =>
                    onInternalSettings?.(editingInternal, {
                      auto_subtask_allow_thread: !editingInternal.auto_subtask_allow_thread,
                    })
                  }
                />
              ) : null}
              <ChangeFormFieldBuilder
                key={editingInternal.group_id}
                embedded
                heading=""
                description=""
                api={{
                  load: () => changesApi.listGroupChecklistFields(editingInternal.group_id),
                  createField: (payload) =>
                    changesApi.createGroupChecklistField(editingInternal.group_id, payload),
                  updateField: (id, payload) =>
                    changesApi.updateGroupChecklistField(editingInternal.group_id, id, payload),
                  deleteField: (id) =>
                    changesApi.deleteGroupChecklistField(editingInternal.group_id, id),
                  reorderFields: (ids) =>
                    changesApi.reorderGroupChecklistFields(editingInternal.group_id, ids),
                }}
              />
            </>
          )}
        </div>
        {instancePackageMode ? null : (
          <Alert variant="light" className="mt-3 mb-0 border">
            Prefere abrir chamado no Incidente com fluxo e fila?
            <div className="mt-2">
              <Button
                type="button"
                size="sm"
                variant="outline-primary"
                onClick={() => {
                  setHeldExternal(editingInternal);
                  onConvertInternalToExternal(editingInternal);
                  openEditExternal(String(editingInternal.group_id));
                }}
              >
                Trocar para chamado
              </Button>
            </div>
          </Alert>
        )}
        <div className="d-flex flex-wrap gap-2 mt-3">
          <Button
            type="button"
            variant="primary"
            disabled={internalPersisting}
            onClick={instancePackageMode ? finishInternalEditAndGoList : goList}
          >
            {internalPersisting ? 'Salvando modelo…' : 'Salvar e voltar à lista'}
          </Button>
          <Button
            type="button"
            variant="outline-secondary"
            onClick={cancelInternalEdit}
            disabled={internalPersisting}
          >
            Voltar sem alterar
          </Button>
        </div>
      </div>
    );
  }

  if (panel === 'edit-external' && editingExternal && canEditExternal) {
    const editingKey = externalLineKey(editingExternal);
    const draft = externalDrafts[editingKey] || {
      selected: true,
      ...emptyIncidentDraft(editingExternal),
    };
    return (
      <div>
        <h6 className="mb-2">Editar chamado no Incidente</h6>
        <p className="small text-muted mb-3">Área: {editingExternal.name}</p>
        <ChangeWorkGroupIncidentModal
          embedded
          hideIncidentKind
          group={editingExternal}
          initialDraft={hydrateIncidentGroupDraft(editingExternal, draft)}
          initialCustomFieldValues={draft.customFieldValues}
          initialCustomFields={draft.customFields}
          targets={{ groups: incidentTargetGroups, workflows }}
          saving={false}
          onHide={cancelExternalEdit}
          onSave={() => undefined}
          users={users}
          onDraftChange={(incidentDraft, extras) => {
            onPatchExternal(editingKey, {
              workflow_id: incidentDraft.incidents_workflow_id || '',
              incidents_group_id: incidentDraft.incidents_group_id,
              title: incidentDraft.incident_title_default,
              comments: incidentDraft.incident_description_default,
              urgency: incidentDraft.incident_urgency_default || '',
              customFieldValues: extras.customFieldValues,
              customFields: extras.customFields,
              open_in_incidents: incidentDraft.open_in_incidents,
              incident_kind: incidentDraft.incident_kind,
              incident_attachment_allowed: incidentDraft.incident_attachment_allowed,
            });
          }}
        />
        {isSiIncidentArea(editingExternal) ? (
          <Alert variant="secondary" className="mt-3 mb-0 small">
            Chamado de <strong>SI (CSI)</strong>: pode travar <strong>Autorizar</strong> conforme o
            fluxo. Preencha todos os campos obrigatórios da solicitação (ex.:{' '}
            <strong>Tipo de Solicitação</strong>) antes de confirmar.
          </Alert>
        ) : null}
        {incidentAllowsAttachments(editingExternal) ? (
          <Form.Group className="mt-3">
            <Form.Label htmlFor="review-external-files">Anexos deste chamado</Form.Label>
            {draft.files.length ? (
              <ul className="list-unstyled small mb-2" aria-label="Arquivos anexados neste chamado">
                {draft.files.map((file, index) => (
                  <li
                    key={`${file.name}-${file.size}-${index}`}
                    className="d-flex align-items-center gap-2 mb-1"
                  >
                    <span>{file.name}</span>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="p-0"
                      onClick={() =>
                        onPatchExternal(editingKey, {
                          files: draft.files.filter((_, itemIndex) => itemIndex !== index),
                        })
                      }
                    >
                      Remover
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="form-text">Nenhum arquivo anexado neste chamado.</div>
            )}
            <Form.Control
              id="review-external-files"
              type="file"
              multiple
              onChange={(event) => {
                const input = event.target as HTMLInputElement;
                const picked = Array.from(input.files || []);
                const merged = [...draft.files];
                picked.forEach((file) => {
                  if (
                    !merged.some(
                      (item) => item.name === file.name && item.size === file.size,
                    )
                  ) {
                    merged.push(file);
                  }
                });
                onPatchExternal(editingKey, { files: merged });
                input.value = '';
              }}
            />
          </Form.Group>
        ) : null}
        <div className="d-flex flex-wrap gap-2 mt-3">
          <Button type="button" variant="primary" onClick={confirmExternalEdit}>
            Salvar e voltar à lista
          </Button>
          <Button type="button" variant="outline-secondary" onClick={cancelExternalEdit}>
            Voltar sem alterar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        {selectedInternals.length ? (
          <Badge bg="secondary">{reviewChipLabel(selectedInternals.length, 'interna', 'internas')}</Badge>
        ) : null}
        {selectedExternals.length ? (
          <Badge bg="info" text="dark">
            {reviewChipLabel(selectedExternals.length, 'chamado', 'chamados')}
          </Badge>
        ) : null}
      </div>

      {showInternalSection ? (
      <section className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="small text-muted mb-0">Subtarefa interna</h6>
          {canAdd ? (
            <Button
              type="button"
              size="sm"
              variant="outline-secondary"
              onClick={() => {
                setAddId('');
                setPanel('add-internal');
              }}
            >
              Adicionar interna
            </Button>
          ) : null}
        </div>
        {internals.length ? (
          internals.map((group) => {
            const lineKey = internalLineKey(group);
            const draft = internalDrafts[lineKey] || emptyInternal(group);
            const isLocked = Boolean(draft.locked);
            const internalRemoveLabel: string =
              (draft.title || '').trim() ||
              (allowRepeatSubtaskLines
                ? group.internal_subtask_template_name || group.name
                : group.internal_subtask_template_name
                  ? group.internal_subtask_template_name
                  : group.name) ||
              'esta subtarefa';
            return (
              <div key={`internal-${lineKey}`} className="border rounded p-2 mb-2">
                <div className="d-flex align-items-center gap-2">
                  {renderSelectionCheckbox({
                    id: `review-internal-${lineKey}`,
                    ariaLabel: `Incluir interna de ${internalSlotLabel(group)}`,
                    checked: draft.selected,
                    locked: isLocked,
                    onChange: (checked) => onToggleInternal(lineKey, checked),
                  })}
                  <Badge bg="light" text="dark">
                    Interna
                  </Badge>
                  {isLocked ? (
                    <Badge bg="secondary" className="ms-1">
                      Já na mudança
                    </Badge>
                  ) : instancePackageMode ? (
                    <Badge bg="warning" text="dark" className="ms-1">
                      Aguardando disparo
                    </Badge>
                  ) : null}
                  <span className="fw-semibold">
                    {allowRepeatSubtaskLines
                      ? (draft.title || '').trim() || group.name
                      : group.internal_subtask_template_name || group.name}
                  </span>
                  <span className="text-muted small text-truncate">
                    {allowRepeatSubtaskLines
                      ? group.name
                      : group.internal_subtask_template_name
                        ? group.name
                        : draft.title}
                  </span>
                  {((instancePackageMode ? rowCanMutateInternal(group) : canEditInternal) &&
                  !isLocked) ? (
                    <Button
                      size="sm"
                      variant="link"
                      className="ms-auto p-0"
                      onClick={() => openEditInternal(lineKey)}
                    >
                      Editar
                    </Button>
                  ) : (
                    <span className="ms-auto" />
                  )}
                  {canRemove &&
                  !isLocked &&
                  (instancePackageMode ? rowCanMutateInternal(group) : canEditInternal) ? (
                    <Button
                      size="sm"
                      variant="link"
                      className="text-danger p-0"
                      onClick={() => void confirmRemoveInternal(lineKey, internalRemoveLabel)}
                    >
                      Remover
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })
        ) : (
          <p className="small text-muted mb-0">Nenhuma subtarefa interna nesta criação.</p>
        )}
      </section>
      ) : null}

      <section>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="small text-muted mb-0">Subtarefa externa (Chamados no Incidente)</h6>
          {canAdd ? (
            <Button
              type="button"
              size="sm"
              variant="outline-secondary"
              onClick={() => {
                setAddId('');
                setPanel('add-external');
              }}
            >
              Adicionar chamado
            </Button>
          ) : null}
        </div>
        {externals.length ? (
          externals.map((group) => {
            const lineKey = externalLineKey(group);
            const draft = externalDrafts[lineKey];
            if (!draft) return null;
            const isLocked = Boolean(draft.locked);
            const externalRemoveLabel: string =
              (draft.title || '').trim() || group.name || 'este chamado';
            return (
              <div key={`external-${lineKey}`} className="border rounded p-2 mb-2">
                <div className="d-flex align-items-center gap-2">
                  {renderSelectionCheckbox({
                    id: `review-external-${lineKey}`,
                    ariaLabel: `Incluir chamado de ${group.name}`,
                    checked: draft.selected,
                    locked: isLocked,
                    onChange: (checked) => onToggleExternal(lineKey, checked),
                  })}
                  <Badge bg="info" text="dark">
                    Chamado
                  </Badge>
                  {isLocked ? (
                    <Badge bg="secondary" className="ms-1">
                      Já na mudança
                    </Badge>
                  ) : instancePackageMode ? (
                    <Badge bg="warning" text="dark" className="ms-1">
                      Aguardando disparo
                    </Badge>
                  ) : null}
                  <span className="fw-semibold">
                    {(draft.title || '').trim() || group.name}
                  </span>
                  {(draft.title || '').trim() ? (
                    <span className="text-muted small text-truncate">{group.name}</span>
                  ) : null}
                  {canEditExternal && !isLocked ? (
                    <Button
                      size="sm"
                      variant="link"
                      className="ms-auto p-0"
                      onClick={() => openEditExternal(lineKey)}
                    >
                      Editar
                    </Button>
                  ) : (
                    <span className="ms-auto" />
                  )}
                  {canRemove && !isLocked ? (
                    <Button
                      size="sm"
                      variant="link"
                      className="text-danger p-0"
                      onClick={() => void confirmRemoveExternal(lineKey, externalRemoveLabel)}
                    >
                      Remover
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })
        ) : (
          <p className="small text-muted mb-0">Nenhuma subtarefa externa nesta criação.</p>
        )}
      </section>
    </div>
  );
};

export default ChangeCreateSubtaskReview;
